import { useMemo } from 'react'

import { listWallets, WalletInfo } from '@/config'
import { useSolanaAdapters } from '@/hooks/wallets/use-sol-wallets'
import { useEvmAdapters } from '@/hooks/wallets/use-wagmi-wallet'
import { useSettings } from '@/store'
import { INetwork } from '@/types'
import { isBtcChain, isEvmChain, isMobileDevice, isSolanaChain, SUPPORTED_NETWORK, WalletType } from '@/utils'
import { IWallet } from '@/wallets'

const sortWalletFn = ({
  wallet1,
  wallet2,
  recentWallets,
  selectedNetwork,
}: {
  wallet1: WalletInfo
  wallet2: WalletInfo
  recentWallets: Record<SUPPORTED_NETWORK, string>
  selectedNetwork: INetwork | null
}) => {
  const networkId = selectedNetwork?.networkId as SUPPORTED_NETWORK

  const isRecentA = recentWallets[networkId] === wallet1.key
  const isRecentB = recentWallets[networkId] === wallet2.key
  // Prioritize recently used wallet first
  if (isRecentA && !isRecentB) return -1
  if (isRecentB && !isRecentA) return 1

  const detectedA = wallet1.isDetected()
  const detectedB = wallet2.isDetected()
  if (detectedA && !detectedB) return -1
  if (detectedB && !detectedA) return 1

  const getPriority = (wallet: WalletInfo) => {
    return (
      (isEvmChain(networkId) ? wallet.priorityEvm : isBtcChain(networkId) ? wallet.priorityBtc : wallet.priority) ??
      wallet.priority
    )
  }

  return getPriority(wallet1) - getPriority(wallet2)
}

export const useWalletAdapters = ({
  selectedNetwork,
  connectedWalletInfo,
}: {
  selectedNetwork: INetwork | null
  connectedWalletInfo: IWallet | null
}) => {
  const { adapters: evmAdapters } = useEvmAdapters()
  const { adapters: solAdapters } = useSolanaAdapters()
  const { recentWallets } = useSettings()
  const connectedWalletKey = connectedWalletInfo?.key

  const isWalletSupported = (walletKey: string, network: INetwork | null = selectedNetwork) => {
    if (!network) return true
    return network.wallets?.includes(walletKey) || false
  }

  // old wallets: btc + hardware wallets
  const filteredWallets = useMemo(() => {
    let wallets = listWallets.filter((w) => w.priority !== -1)

    if (isMobileDevice()) {
      wallets = wallets.filter((w) => w.type !== WalletType.WALLET_TYPE_HARDWARE_WALLET)
    }

    if (selectedNetwork) {
      wallets = wallets.filter((w) => selectedNetwork.wallets?.includes(w.key))
    }

    // pc show all
    // mobile browser show all
    // mobile wallet browser show 1
    if (isMobileDevice() && wallets.some((e) => e.isDetected())) wallets = wallets.filter((w) => w.isDetected())

    return wallets.sort((wallet1, wallet2) => {
      return sortWalletFn({ wallet1, wallet2, recentWallets, selectedNetwork })
    })
  }, [selectedNetwork, listWallets])

  // kit wallets: evm + sol
  const sortKitAdapters: WalletInfo[] = useMemo(() => {
    return (
      isEvmChain(selectedNetwork?.networkId)
        ? evmAdapters
        : isSolanaChain(selectedNetwork?.networkId)
          ? solAdapters
          : []
    ).sort((wallet1, wallet2) => {
      return sortWalletFn({ wallet1, wallet2, recentWallets, selectedNetwork })
    })
  }, [evmAdapters, selectedNetwork, solAdapters])

  const kitWallets = sortKitAdapters.map((wallet) => {
    const isRecent = recentWallets[selectedNetwork?.networkId as SUPPORTED_NETWORK] === wallet.key
    const isConnected = connectedWalletInfo?.walletId === wallet.key
    return { wallet, isSupported: true, isRecent, isConnected, key: wallet.key }
  })

  const otherWallets = filteredWallets.map((wallet) => {
    const isSupported = isWalletSupported(wallet.key)
    const isRecent = recentWallets[selectedNetwork?.networkId as SUPPORTED_NETWORK] === wallet.key
    const isConnected = connectedWalletKey === wallet.key
    return { wallet, isSupported, isRecent, isConnected, key: wallet.key }
  })

  return kitWallets.concat(otherWallets)
}
